import { Box, Card, CardContent, Typography, IconButton } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import { useState, useEffect, useCallback } from "react";
import { IStreamDetails } from "../types/memorial-types";

// ─── Speaker color ────────────────────────────────────────────────────────────
export const SPEAKER_COLORS = ["#005CFC"];

// ─── Countdown badge ──────────────────────────────────────────────────────────
function useStreamCountdown(streams: IStreamDetails[]): string {
  const getSecondsUntil = useCallback(() => {
    const now = new Date();
    for (const s of streams) {
      if (s.date && s.start) {
        const t = new Date(`${s.date}T${s.start}+02:00`);
        if (!isNaN(t.getTime()) && t > now) {
          return Math.max(0, Math.floor((t.getTime() - now.getTime()) / 1000));
        }
      }
    }
    return 0;
  }, [streams]);

  const [secs, setSecs] = useState(getSecondsUntil);

  useEffect(() => {
    setSecs(getSecondsUntil());
    const id = setInterval(() => setSecs((t) => (t > 0 ? t - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [getSecondsUntil]);

  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const sc = String(secs % 60).padStart(2, "0");
  return `${h}:${m}:${sc}`;
}

export function CountdownBadge({ streams }: { streams: IStreamDetails[] }) {
  const countdown = useStreamCountdown(streams);
  if (countdown === "00:00:00") return null;
  return (
    <Box
      sx={{
        bgcolor: "#FF6F00",
        color: "#fff",
        borderRadius: 2,
        px: 2,
        py: 0.75,
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        minWidth: 120,
        justifyContent: "center",
      }}
    >
      <AccessTimeIcon sx={{ fontSize: 16 }} />
      <Typography variant="h6" sx={{ letterSpacing: 2, fontSize: "1.05rem" }}>
        {countdown}
      </Typography>
    </Box>
  );
}

// ─── Speaker card ─────────────────────────────────────────────────────────────
export interface SpeakerCardProps {
  name: string;
  role: string;
  image?: string;
  onDelete?: () => void;
}

export function SpeakerCard({ name, role, image, onDelete }: SpeakerCardProps) {
  return (
    <Card
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        transition: "transform 0.2s, box-shadow 0.2s",
        position: "relative",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 6px 16px rgba(0,0,0,0.16)",
        },
      }}
    >
      {/* Delete button */}
      {onDelete && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            zIndex: 1,
            bgcolor: "rgba(0,0,0,0.45)",
            color: "#fff",
            width: 22,
            height: 22,
            "&:hover": { bgcolor: "error.main" },
          }}
        >
          <DeleteIcon sx={{ fontSize: 12 }} />
        </IconButton>
      )}

      {/* Image area — square aspect ratio */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingTop: "100%",
          overflow: "hidden",
          bgcolor: "#e0e0e0",
        }}
      >
        {image ? (
          <Box
            component="img"
            src={image}
            alt={name}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top",
              transition: "transform 0.3s",
              "&:hover": { transform: "scale(1.05)" },
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#c5cae9",
            }}
          >
            <PersonIcon sx={{ fontSize: 44, color: "#fff" }} />
          </Box>
        )}
      </Box>

      {/* Footer banner */}
      <Box
        sx={{
          bgcolor: "#005CFC",
          px: 1,
          py: 1,
          textAlign: "center",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "#fff",
            fontSize: "0.7rem",
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.8)",
            fontSize: "0.62rem",
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {role}
        </Typography>
      </Box>
    </Card>
  );
}

// ─── Live preview card ────────────────────────────────────────────────────────
export function LivePreviewCard({ stream }: { stream: IStreamDetails }) {
  const eventName = stream.title || stream.booking?.service || "Live Event";

  const dateLabel = stream.date
    ? new Date(stream.date).toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
      }}
    >
      <CardContent>
        {/* Brand row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2,
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.48rem",
                fontWeight: 700,
                textAlign: "center",
                lineHeight: 1.3,
                color: "primary.main",
              }}
            >
              {eventName.toUpperCase()}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "text.primary", fontSize: "0.82rem" }}
            >
              {eventName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.72rem" }}
            >
              {stream.booking?.service}
            </Typography>
          </Box>
        </Box>

        {/* LIVE badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Box
            sx={{
              bgcolor: "#D32F2F",
              px: 1,
              py: 0.3,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <FiberManualRecordIcon sx={{ fontSize: 10, color: "#fff" }} />
            <Typography
              sx={{ color: "#fff", fontSize: "0.68rem", fontWeight: 700 }}
            >
              LIVE STREAM
            </Typography>
          </Box>
        </Box>

        {/* Thumbnail */}
        {stream.stream_thumbnail_background_image && (
          <Box
            component="img"
            src={stream.stream_thumbnail_background_image}
            sx={{
              width: "100%",
              height: 120,
              objectFit: "cover",
              borderRadius: 2,
              mb: 1.5,
            }}
          />
        )}

        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "0.68rem" }}
        >
          {dateLabel}
        </Typography>
      </CardContent>
    </Card>
  );
}
